import {
  BoardMeetingState,
  boardMeetingStateTimeline,
} from "@boark/shared/types/board-meeting";
import { BoardMeetingWithPeople } from "@boark/shared/types/board-meeting-with-people";
import {
  canDeleteBoardMeeting,
  canEditBoardMeetingDetails,
} from "@boark/shared/utils/board-meeting";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetBoardMeetings } from "../../queries/board-meeting";
import { useGetAuthContext } from "../../queries/get-auth-context";
import { formatUserName } from "../../services/board-meeting";
import { useLocale } from "../../stores/locale";
import { formatDateTime } from "../../utils/date";
import { recordIdToId } from "../../utils/record-id-to-id";
import { BoardMeetingCreate } from "./board-meeting-create";
import { BoardMeetingDelete } from "./board-meeting-delete";

export function BoardMeetingList() {
  const { locale: t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: authContext } = useGetAuthContext();
  const { data: meetings, isLoading } = useGetBoardMeetings();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Routes>
        <Route element={<BoardMeetingCreate />} path="/create" />
        <Route element={<BoardMeetingDelete />} path="/:id/delete" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.boardMeeting.boardMeetings}</H1>
          <Button route="/admin/board-meeting/list/create">
            {t.boardMeeting.create}
          </Button>
        </div>
        <Table
          data={meetings || []}
          editRouteButton={(row) =>
            canEditBoardMeetingDetails(row, authContext?.id)
              ? `/admin/board-meeting/${recordIdToId(row.id)}/edit`
              : ""
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          loading={isLoading}
          locale={{
            yes: t.yes,
            no: t.no,
            actions: t.action.actions,
            empty: t.empty,
          }}
          removeRouteButton={(row) =>
            canDeleteBoardMeeting(row, authContext?.id)
              ? `/admin/board-meeting/list/${recordIdToId(row.id)}/delete`
              : ""
          }
          sortBy={searchParams.get("sortBy") || "date"}
          sortByDirection={
            (searchParams.get("sortByDirection") as "asc" | "desc") || "desc"
          }
          viewRouteButton={(row) =>
            `/admin/board-meeting/${recordIdToId(row.id)}`
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({ filterBy: JSON.stringify(value) });
          }}
          onSort={(field, direction) => {
            updateSearchParams({ sortBy: field, sortByDirection: direction });
          }}
        >
          <Column field="name">{t.boardMeeting.name}</Column>
          <Column
            body={(row: BoardMeetingWithPeople) =>
              formatDateTime(row.date, false)
            }
            field="date"
            filterableBy="date"
          >
            {t.boardMeeting.date}
          </Column>
          <Column
            body={(row: BoardMeetingWithPeople) => row.location || "-"}
            field="location"
            filterableBy="text"
          >
            {t.boardMeeting.location}
          </Column>
          <Column
            body={(row: BoardMeetingWithPeople) =>
              t.boardMeeting.states[row.state]
            }
            field="state"
            filterableBy="select"
            filterOptions={() =>
              boardMeetingStateTimeline.map((state: BoardMeetingState) => ({
                label: t.boardMeeting.states[state],
                value: state,
              }))
            }
          >
            {t.boardMeeting.state}
          </Column>
          <Column
            body={(row: BoardMeetingWithPeople) => formatUserName(row.caller)}
            field="caller.email"
            filterableBy="text"
          >
            {t.boardMeeting.caller}
          </Column>
          <Column
            body={(row: BoardMeetingWithPeople) => String(row.called.length)}
            field="called"
            sortable={false}
          >
            {t.boardMeeting.called}
          </Column>
        </Table>
      </div>
    </>
  );
}
